import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/auth';

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication. Redirects to login if not authenticated.
 * Optionally checks for specific user roles.
 */

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/login'
}: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading indicator while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified.
  // `professional_admin` is a strict superset of `company_admin` — accept it
  // anywhere company_admin is required (mirrors the server-side requireRole).
  if (requiredRoles && requiredRoles.length > 0 && user) {
    const allowed = new Set<UserRole>(requiredRoles);
    if (allowed.has('company_admin')) { allowed.add('professional_admin'); allowed.add('consulting_admin'); }
    if (!allowed.has(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-8">
              You don't have permission to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and has required role
  return <>{children}</>;
}

/**
 * Admin-only route guard
 */
export function AdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ProtectedRoute requiredRoles={['platform_admin', 'company_admin']}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Company Admin-only route guard
 */
export function CompanyAdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ProtectedRoute requiredRoles={['company_admin']}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Professional Admin route guard — the Professional Edition interface (`/pro`).
 * Requires the `professional_admin` role AND the company on the edition (or a
 * platform_admin for support). See Pro-Dashboard.md §4.
 */
export function ProfessionalAdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, company } = useAuth();
  const role = user?.role;
  const editionOn = !!company?.professional_edition_enabled;
  const ok = role === 'platform_admin' || (role === 'professional_admin' && editionOn);
  return (
    <ProtectedRoute>
      {ok ? <>{children}</> : <Navigate to="/dashboard" replace />}
    </ProtectedRoute>
  );
}

/**
 * CRM access (Nivoon-CRM.md): Professional Edition ON + a CRM-capable role
 * (admins + sales roles). Mirrors the server crmGuard.
 */
export function CrmRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, company } = useAuth();
  const role = user?.role;
  const editionOn = !!company?.professional_edition_enabled;
  const crmRoles = ['platform_admin', 'professional_admin', 'company_admin', 'sales_user', 'account_manager'];
  const ok = role === 'platform_admin' || (editionOn && !!role && crmRoles.includes(role));
  return (
    <ProtectedRoute>
      {ok ? <>{children}</> : <Navigate to="/dashboard" replace />}
    </ProtectedRoute>
  );
}

/**
 * Consulting edition guard (Nivoon-Consulting.md §4). Consulting Edition ON + a
 * consulting-capable role (admins + consultant/consulting_manager).
 */
export function ConsultingRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, company } = useAuth();
  const role = user?.role;
  const editionOn = !!company?.consulting_edition_enabled;
  const consultingRoles = ['platform_admin', 'consulting_admin', 'consultant', 'consulting_manager'];
  const ok = role === 'platform_admin' || (editionOn && !!role && consultingRoles.includes(role));
  return (
    <ProtectedRoute>
      {ok ? <>{children}</> : <Navigate to="/dashboard" replace />}
    </ProtectedRoute>
  );
}

/**
 * Platform Admin-only route guard
 */
export function PlatformAdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ProtectedRoute requiredRoles={['platform_admin']}>
      {children}
    </ProtectedRoute>
  );
}

export default ProtectedRoute;
